/**
 * Turn-by-turn directions from one stop to the next.
 *
 * Walking and driving come from Valhalla (FOSSGIS public instance), which
 * returns real maneuver-by-maneuver instructions. Transit is attempted against
 * Transitous (MOTIS over community GTFS) — it routes for real in the
 * Netherlands and increasingly elsewhere; where the feeds run out (much of
 * Dalmatia) it degrades to the trip's researched notes for that stop plus a
 * maps deep link.
 */
import { computeDay, type SlotRowComp } from "@trip/shared/engine/schedule";
import { esc, fmin, t12 } from "@trip/shared/engine/time";
import type { LatLng } from "@trip/shared/engine/travel";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";
import { ico } from "./icons.js";

const VALHALLA = "https://valhalla1.openstreetmap.de/route";
const TRANSITOUS = "https://api.transitous.org/api/v1/plan";

type Mode = "pedestrian" | "auto" | "transit";

interface Endpoint {
  name: string;
  geo: LatLng;
  /** the destination activity, when the next stop is one */
  actId?: string;
  timeLabel?: string;
  /** RFC3339 departure estimate, for transit routing */
  depIso?: string;
}

interface Maneuver {
  instruction: string;
  /** miles for this step (units requested from Valhalla) */
  length: number;
  /** seconds */
  time: number;
}

const dist = (mi: number) => (mi < 0.15 ? `${Math.round((mi * 5280) / 10) * 10} ft` : `${mi.toFixed(1)} mi`);
const mins = (s: number) => `${Math.max(1, Math.round(s / 60))} min`;

async function valhalla(from: LatLng, to: LatLng, costing: "pedestrian" | "auto") {
  const res = await fetch(VALHALLA, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      locations: [
        { lat: from[0], lon: from[1] },
        { lat: to[0], lon: to[1] },
      ],
      costing,
      directions_options: { units: "miles" },
    }),
  });
  const j = await res.json();
  if (!res.ok || j.error) throw new Error(j.error || `routing failed (${res.status})`);
  const leg = j.trip?.legs?.[0];
  return {
    summary: { length: j.trip.summary.length as number, time: j.trip.summary.time as number },
    maneuvers: (leg?.maneuvers ?? []) as Maneuver[],
  };
}

/* ---------------- live transit via Transitous ---------------- */

interface TransitLeg {
  mode: string; // WALK, TRAM, BUS, RAIL, FERRY, …
  routeShortName?: string;
  headsign?: string;
  from: { name: string };
  to: { name: string };
  startTime: string; // RFC3339, UTC
  endTime: string;
}

interface Itinerary {
  duration: number;
  startTime: string;
  endTime: string;
  transfers: number;
  legs: TransitLeg[];
}

async function transitousPlan(from: LatLng, to: LatLng, depIso: string | undefined): Promise<Itinerary[]> {
  const u = new URL(TRANSITOUS);
  u.searchParams.set("fromPlace", `${from[0]},${from[1]}`);
  u.searchParams.set("toPlace", `${to[0]},${to[1]}`);
  if (depIso) u.searchParams.set("time", depIso);
  u.searchParams.set("arriveBy", "false");
  const res = await fetch(u.toString());
  if (!res.ok) throw new Error(`transit routing failed (${res.status})`);
  const j = (await res.json()) as { itineraries?: Itinerary[] };
  return j.itineraries ?? [];
}

const MODE_ICON: Record<string, string> = {
  WALK: "🚶",
  TRAM: "🚊",
  BUS: "🚌",
  RAIL: "🚆",
  REGIONAL_RAIL: "🚆",
  REGIONAL_FAST_RAIL: "🚆",
  LONG_DISTANCE: "🚆",
  HIGHSPEED_RAIL: "🚆",
  SUBWAY: "🚇",
  METRO: "🚇",
  FERRY: "⛴",
  COACH: "🚌",
};

/**
 * Legs arrive with UTC timestamps; render them in the trip's local clock.
 * Every place this trip touches with transit data sits at UTC+2 in September
 * (Amsterdam and Croatia share CEST) — when trips span real offsets this
 * should switch to a per-activity timezone.
 */
function legTime(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  }).format(new Date(iso));
}

const isAir = (m: string) => /AIR|PLANE|FLIGHT/i.test(m);

/**
 * Transitous carries flight feeds, and a destination AT an airport snaps to
 * its aviation node — itineraries arrive by rail and then keep "going" onto
 * planes. Everything from the first air leg onward is that artifact: cut
 * there. An itinerary that STARTS with air legs (origin snapped the same way)
 * sheds them from the front first. Nothing rideable left → not an itinerary.
 */
function groundOnly(it: Itinerary): Itinerary | null {
  let legs = [...it.legs];
  while (legs.length && (isAir(legs[0].mode) || (legs[0].mode === "WALK" && legs[1] && isAir(legs[1].mode)))) legs.shift();
  const firstAir = legs.findIndex((l) => isAir(l.mode));
  if (firstAir !== -1) legs = legs.slice(0, firstAir);
  if (!legs.some((l) => l.mode !== "WALK")) return null;
  const start = legs[0].startTime;
  const end = legs[legs.length - 1].endTime;
  return {
    ...it,
    legs,
    startTime: start,
    endTime: end,
    duration: Math.max(60, (new Date(end).getTime() - new Date(start).getTime()) / 1000),
    transfers: Math.max(0, legs.filter((l) => l.mode !== "WALK").length - 1),
  };
}

function transitItineraryHTML(its: Itinerary[]): string {
  const it = its[0];
  const ride = it.legs.filter((l) => l.mode !== "WALK");
  const label = ride.length
    ? ride.map((l) => `${MODE_ICON[l.mode] ?? "🚌"} ${l.routeShortName ?? l.mode}`).join(" → ")
    : "walking";
  const alternates = its
    .slice(1, 4)
    .map((x) => legTime(x.startTime))
    .join(", ");
  return `
    <div class="dir-summary"><b>${legTime(it.startTime)} → ${legTime(it.endTime)}</b> · ${mins(it.duration)} · ${label}${
      it.transfers ? ` · ${it.transfers} transfer${it.transfers > 1 ? "s" : ""}` : ""
    }</div>
    <ol class="dir-steps">${it.legs
      .map((l) =>
        l.mode === "WALK"
          ? `<li><span class="dir-instr">🚶 Walk to ${esc(l.to.name === "END" ? "your stop" : l.to.name)}</span><span class="dir-dist">${legTime(l.startTime)}</span></li>`
          : `<li><span class="dir-instr">${MODE_ICON[l.mode] ?? "🚌"} <b>${esc(l.routeShortName ?? l.mode)}</b>${
              l.headsign ? ` toward ${esc(l.headsign)}` : ""
            } — board at ${esc(l.from.name)}</span><span class="dir-dist">${legTime(l.startTime)}–${legTime(l.endTime)}</span></li>`
      )
      .join("")}</ol>
    ${alternates ? `<div class="dir-note">Later departures: ${alternates}. Live times come from published timetables — confirm locally for ferries.</div>` : ""}`;
}

/**
 * Best-effort RFC3339 departure time for transit routing. `iso` is authoritative
 * when a day carries it; the display date plus the trip's year is the fallback
 * for the extracted definition. The +02:00 matches everywhere this trip has
 * transit coverage in September (CEST).
 */
function depIsoFor(displayDate: string, iso: string | undefined, minutes: number): string | undefined {
  let ymd = iso;
  if (!ymd) {
    const d = new Date(`${displayDate} 2026 12:00:00`);
    if (Number.isNaN(d.getTime())) return undefined;
    ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  return `${ymd}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00+02:00`;
}

/** Walks the day's computed rows to find what comes after slot `i` — in
 * temporal order, which places extra ("x1"…) rows after the definition's last
 * flexible slot. */
function nextStop(dayId: string, fromIndex: number | string): { from: Endpoint; to: Endpoint } | null {
  const def = store.def;
  const day = def.days.find((d) => d.id === dayId);
  if (!day) return null;
  const comp = computeDay(def, day, store.slots, store.dayEnds[dayId], store.fixDurs);

  /* rows in the order the day actually runs */
  const order: { row: (typeof comp.rows)[number]; item?: (typeof day.items)[number] }[] = [];
  const extras = comp.rows.slice(day.items.length);
  day.items.forEach((item, i) => {
    order.push({ row: comp.rows[i], item });
    if (comp.extra && i === comp.extra.afterIndex) for (const r of extras) order.push({ row: r });
  });
  if (!comp.extra && extras.length) for (const r of extras) order.push({ row: r });

  const fromPos = order.findIndex((o) => o.row?.kind === "slot" && String(o.row.i) === String(fromIndex));
  const fromRow = order[fromPos]?.row as SlotRowComp | undefined;
  const fromSlot = store.slots[`${dayId}_${fromIndex}`];
  if (!fromRow || fromRow.skipped || !fromRow.geo || !fromSlot) return null;
  const from: Endpoint = {
    name: def.act[fromSlot.act].name.split("—")[0].trim(),
    geo: fromRow.geo,
    timeLabel: fmin(fromRow.endMin),
    depIso: depIsoFor(day.date, day.iso, fromRow.endMin),
  };

  for (let p = fromPos + 1; p < order.length; p++) {
    const { row, item } = order[p];
    if (item && isFixed(item)) {
      /**
       * A fixed item with an end time is a journey (a flight, a ferry): its
       * `g` is where it LANDS — a map pin, not a destination you can be routed
       * to from here. Routing "to the flight" once sent someone from Utrecht
       * toward Dubrovnik airport via a Cagliari connection. The day continues
       * in another place; there is no next stop to walk to.
       */
      if (item.e && item.g) return null;
      const g = item.g ?? (item.at ? def.hotelPts[item.at] : undefined);
      if (g) return { from, to: { name: item.l.split("·")[0].trim(), geo: g, timeLabel: t12(item.t) } };
      continue;
    }
    const r = row as SlotRowComp;
    const slot = store.slots[`${dayId}_${r.i}`];
    if (!slot || r.skipped || !r.geo) continue;
    return {
      from,
      to: {
        name: def.act[slot.act].name.split("—")[0].trim(),
        geo: r.geo,
        actId: slot.act,
        timeLabel: fmin(r.startMin),
      },
    };
  }

  // nothing left today — head back to base
  const home = def.dayHome[dayId];
  if (home && !def.noEnd[dayId] && def.hotelPts[home[1]]) {
    return { from, to: { name: "Back to your base", geo: def.hotelPts[home[1]], timeLabel: comp.backAtBase ? fmin(comp.backAtBase.min) : "" } };
  }
  return null;
}

const mapsUrl = (from: LatLng, to: LatLng, mode: Mode) =>
  `https://www.google.com/maps/dir/?api=1&origin=${from[0]},${from[1]}&destination=${to[0]},${to[1]}&travelmode=${
    mode === "auto" ? "driving" : mode === "transit" ? "transit" : "walking"
  }`;

function shell(from: Endpoint, to: Endpoint, mode: Mode, body: string): string {
  const tab = (m: Mode, label: string) =>
    `<button class="dir-tab${m === mode ? " on" : ""}" data-action="dir-mode" data-mode="${m}">${label}</button>`;
  return `
    <div class="m-head"><div class="m-cat">Directions</div><h3>${esc(from.name)} → ${esc(to.name)}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body">
      <div class="dir-tabs">${tab("pedestrian", `${ico.walk} Walk`)}${tab("auto", `${ico.car} Drive`)}${tab("transit", `${ico.bus} Transit`)}</div>
      ${body}
      <div class="dir-foot">
        <a href="${mapsUrl(from.geo, to.geo, mode)}" target="_blank" rel="noopener">Open in Google Maps ↗</a>
        ${to.timeLabel ? `<span class="dir-eta">Scheduled arrival ${esc(to.timeLabel)}</span>` : ""}
      </div>
    </div>`;
}

function stepsHTML(r: { summary: { length: number; time: number }; maneuvers: Maneuver[] }): string {
  return `
    <div class="dir-summary"><b>${dist(r.summary.length)}</b> · about <b>${mins(r.summary.time)}</b></div>
    <ol class="dir-steps">${r.maneuvers
      .map(
        (m) =>
          `<li><span class="dir-instr">${esc(m.instruction)}</span>${
            m.length > 0.005 ? `<span class="dir-dist">${dist(m.length)}</span>` : ""
          }</li>`
      )
      .join("")}</ol>`;
}

/** Fallback when live routing has nothing: the trip's researched notes. */
function transitNotesHTML(to: Endpoint): string {
  const note = to.actId ? store.def.act[to.actId]?.transport : undefined;
  return `
    ${
      note
        ? `<div class="dir-summary">How people get there</div><div class="dir-transit">${t12(note)}</div>`
        : `<div class="dir-transit dir-muted">No live transit route found and no notes recorded for this stop.</div>`
    }
    <div class="dir-note">No published timetable covers this leg (much of Dalmatia's bus and ferry data isn't open), so the link below opens it in a maps app in transit mode.</div>`;
}

let current: { from: Endpoint; to: Endpoint } | null = null;

export async function openDirections(dayId: string, index: number | string, mode: Mode = "pedestrian") {
  const pair = current ?? nextStop(dayId, index);
  if (!pair) {
    banner("Directions not available — location data is missing for one or more activities.", "warn");
    return;
  }
  current = pair;
  const { from, to } = pair;
  const modal = document.getElementById("modal")!;
  modal.style.maxWidth = "620px";
  modal.dataset.dirDay = dayId;
  modal.dataset.dirIndex = String(index);
  document.getElementById("modalbg")!.classList.add("open");

  if (mode === "transit") {
    modal.innerHTML = shell(from, to, mode, `<div class="dir-summary dir-muted">Looking up timetables…</div>`);
    try {
      const its = (await transitousPlan(from.geo, to.geo, from.depIso))
        .map(groundOnly)
        .filter((x): x is Itinerary => x !== null);
      if (!document.getElementById("modalbg")!.classList.contains("open")) return;
      modal.innerHTML = shell(
        from,
        to,
        mode,
        its.length && its[0].legs.some((l) => l.mode !== "WALK") ? transitItineraryHTML(its) : transitNotesHTML(to)
      );
    } catch {
      modal.innerHTML = shell(from, to, mode, transitNotesHTML(to));
    }
    return;
  }

  modal.innerHTML = shell(from, to, mode, `<div class="dir-summary dir-muted">Fetching directions…</div>`);
  try {
    const r = await valhalla(from.geo, to.geo, mode);
    if (!document.getElementById("modalbg")!.classList.contains("open")) return;
    modal.innerHTML = shell(from, to, mode, stepsHTML(r));
  } catch {
    modal.innerHTML = shell(
      from,
      to,
      mode,
      `<div class="dir-transit dir-muted">Couldn't fetch turn-by-turn directions — you may be offline. The maps link below still works once you're back online.</div>`
    );
  }
}

/** Switching tabs inside an open directions modal. */
export function switchDirectionsMode(mode: Mode) {
  const modal = document.getElementById("modal")!;
  const raw = modal.dataset.dirIndex ?? "0";
  openDirections(modal.dataset.dirDay!, /^x\d+$/.test(raw) ? raw : Number(raw), mode);
}

export function clearDirections() {
  current = null;
}
